import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { type CategorySchema, SoftwareApi, type SoftwareSchema, VersionsApi, type VersionSchema } from 'openapi'
import { useParentCategories } from 'features/categories/hooks'
import type { Crumb } from 'ui/BreadCrumbs/interfaces'
import { CategoryRoutePath } from 'router'
import type { VersionStatus } from 'features/versions/types'
import { useInstalledComponents } from 'features/app/hooks'
import { compareVersions } from 'utils/version-string/version-string'

export function useSoftwareQuery(softwareId: number) {
  return useQuery({ queryKey: ['getSoftware', softwareId], queryFn: () => SoftwareApi.get({ id: softwareId }) })
}

export function useVersionQuery(versionId?: number) {
  const resp = useQuery<VersionSchema | undefined>({
    queryKey: ['getVersion', versionId],
    queryFn: async () => {
      if (!versionId) return undefined
      return VersionsApi.get({ id: versionId })
    },
  })
  return {
    ...resp,
    version: resp.data,
  }
}

export function useSoftwarePageCrumbs(software?: Pick<SoftwareSchema, 'id' | 'name' | 'category_id'>): Crumb[] {
  const parentCategories = useParentCategories(software?.category_id)
  const crumbs = useMemo<Crumb[]>(() => {
    if (!software) return []

    const categoriesCrumbs: Crumb[] = parentCategories.map((cat: CategorySchema, i) => {
      const last = i === parentCategories.length - 1
      return {
        label: cat.name,
        href: last ? CategoryRoutePath({ categoryId: cat.id }) : null,
      }
    })

    return categoriesCrumbs
  }, [software, parentCategories])
  return crumbs
}

export function useVersionStatus(version?: VersionSchema): VersionStatus | undefined {
  const { installedComponents } = useInstalledComponents()

  const versionStatus = useMemo((): VersionStatus | undefined => {
    if (!version) return undefined
    if (!version.components.some((name) => installedComponents.some((comp) => comp.filename === name))) {
      return 'notInstalled'
    }

    if (version.components.every((name) => installedComponents.some((comp) => comp.filename === name))) {
      return 'installed'
    }

    return 'partiallyInstalled'
  }, [installedComponents, version])

  return versionStatus
}

export function useUpdateAvailable(version?: VersionSchema): boolean {
  const { installedComponents } = useInstalledComponents()

  return useMemo(() => {
    if (!version) return false

    return version.components.some((name) => {
      const installedComponent = installedComponents.find((comp) => comp.filename === name)
      if (!installedComponent) return false
      if (!version.version) return false
      return compareVersions(version.version, installedComponent.version) > 0
    })
  }, [installedComponents, version])
}
